import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { BehaviorSubject, fromEvent, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
import {
  FacilitiesService,
  OnePatientOnServerDeleted,
  PatientModel,
  PatientsDataSource,
  PatientsPageRequested,
  PatientsService,
  PatientStatusOnServerUpdated,
  selectPatientErrorState,
  selectPatientsActionLoading,
  ViewContext,
} from '../../../../../../core/lists';
import { TabletUtil } from '../../../../../../core/utils';
import { MY_FORMATS } from '../patient-edit/patient-edit.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { TaskService } from '../../../../../../core/timeline';
import { FilteredViewService } from '../../../../../../core/services/filtered-view.service';
import { MatSelect } from '@angular/material/select';
import { NgxPermissionsService } from 'ngx-permissions';
import { Moment, utc } from 'moment';
import { ZoomInEntityComponent } from '../../../../../partials/content/general/zoom-in-entity/zoom-in-entity.component';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import * as moment from 'moment';
import { DownloadService } from 'src/app/shared/utils/download.service';

@Component({
  selector: 'kt-patients-list',
  templateUrl: './patients-list.component.html',
  styleUrls: ['./patients-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
  ],
})
export class PatientsListComponent implements OnInit, OnDestroy {
  dataSource: PatientsDataSource;
  displayedColumns = [
    'entityId',
    'profile',
    'name',
    'dob',
    'cellPhone',
    'isHoldInHospital',
    'isActive',
    'actions',
  ];
  searchBarColumns = [
    'searchEntityId',
    'searchProfile',
    'searchNameEmail',
    'searchDOB',
    'searchCellPhone',
    'searchIsHoldInHospital',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchEntityIdInput = '';
  searchNameEmailInput = '';
  searchDOBInput: Moment | any;
  @ViewChild('searchDOBPicker') searchDOBPicker: MatDatepicker<Moment>;
  searchCellPhoneInput = '';
  searchIsActiveSelect = 'true';
  searchIsHoldInHospitalSelect = '';
  @ViewChild('searchFacilityInput') searchFacilityInput: ElementRef;
  @ViewChild('searchFacilitySelect') searchFacilitySelect: MatSelect;
  searchFacilityTimer;
  facilities$ = new BehaviorSubject([]);
  statusTracker = 0;
  private subscriptions: any[] = [];
  hasSubmissionErrors = false;
  submissionErrorObj: any;
  file: File;
  attachment: string;
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  uploadForm: FormGroup;
  isViewMode = false;
  file1: File;
  attachment1: string;
  @ViewChild('fileInput1', { static: true }) fileInput1: ElementRef;
  uploadForm1: FormGroup;
  // tslint:disable-next-line:no-output-on-prefix
  @Output() navigateToPatient = new EventEmitter<any>();
  @Input() filteredView;
  @Input() entityType;
  @Input() filterEntities;
  @Input() canvas;
  @Output() totalCount = new EventEmitter<any>();
  @Output() changeTabToResident = new EventEmitter<any>();
  @Input() fromDoctor;
  @Input() fromFacility;
  @Input() entity_id_for_task_list;
  entity_id; // for get entity id if opened from facility or doctor
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize;
  exporting = false;
  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private tabletUtil: TabletUtil,
    private patientsService: PatientsService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private downloadService: DownloadService,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private filteredViewService: FilteredViewService,
    public stringUtils: StringUtilsService,
    private taskService: TaskService,
    private facilitiesService: FacilitiesService,
    private permissionsService: NgxPermissionsService,
    private navigationService: NavigationService,
  ) {}

  ngOnInit(): void {
    if (this.fromFacility || this.fromDoctor)
      this.activatedRoute.params.subscribe((params) => {
        this.entity_id = this.entity_id_for_task_list ? this.entity_id_for_task_list : params.id;
      });
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    if (this.activatedRoute.url[`_value`][1]?.path === 'view') {
      this.isViewMode = true;
    }
    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'name';
      this.sort.direction = this.sort.direction || 'asc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadPatientsList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadPatientsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });
    this.dataSource = new PatientsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe();
    this.subscriptions.push({ id: 4, sub: entitiesSubscription });
    this.uploadForm = this.formBuilder.group({
      profile: [''],
    });

    this.uploadForm1 = this.formBuilder.group({
      profile1: [''],
    });
    this.dataSource.paginatorTotal$.subscribe((res) => {
      this.totalCount.emit(res);
    });

    if (!this.fromFacility) {
      this.displayedColumns.splice(5, 0, 'facility');
      this.searchBarColumns.splice(5, 0, 'searchFacility');
    }
    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied?.pageSize;

      if (this.filtersApplied.facilities?.length > 0 && !(this.fromFacility || this.fromDoctor)) {
        const facilityFilterSubscription = this.facilitiesService
          .getFacilitiesByIds(this.filtersApplied.facilities)
          .subscribe((facilities) => {
            this.searchFacilityInput.nativeElement.value = facilities.data.map((x) => x.name);
            this.searchFacilitySelect.value = facilities.data;
            this.loadPatientsList();
          });
        this.subscriptions.push({ id: 0, sub: facilityFilterSubscription });
        return;
      }
    }
    // Comment by Nourhan: please don't change the order of this line
    setTimeout(() => this.loadPatientsList());
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => el.sub.unsubscribe());
  }

  loadPatientsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'name',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new PatientsPageRequested({ page: queryParams }));
  }

  formatDate(date: string): string {
    if (!date) return null;

    const dateList = date.split('-');

    dateList.unshift(dateList.pop());

    return dateList.join('-');
  }

  filterConfiguration(reset_applied_flag?): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchEntityIdInput = appliedFilters.entityId;
        this.searchNameEmailInput = appliedFilters.nameEmail;
        if (appliedFilters.dob) {
          this.searchDOBInput = moment(appliedFilters.dob);
        } else {
          this.searchDOBInput = '';
          this.searchDOBPicker?.select(undefined);
        }
        this.searchCellPhoneInput = appliedFilters.cellphone?.replace(/[()\-\s]/g, '');
        this.searchIsHoldInHospitalSelect = appliedFilters.isHoldInHospital;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'asc';
      this.sort.active = this.filtersApplied?.sortActive || 'name';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    const dateBirthIsMoment = moment.isMoment(this.searchDOBInput);
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.entityId = this.searchEntityIdInput;
      filter.nameEmail = this.searchNameEmailInput;
      filter.dob = this.formatDate(
        dateBirthIsMoment ? this.searchDOBInput.format('MM-DD-YYYY') : '',
      );
      filter.cellphone = this.searchCellPhoneInput?.replace(/[()\-\s]/g, '');
      filter.isHoldInHospital = this.searchIsHoldInHospitalSelect;
      filter.showSearchBar = true;
      if (!this.fromFacility)
        filter.facilities = this.searchFacilitySelect?.value?.map((facility) => facility.id) || [];
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    if (this.entityType === 'DOCTOR') filter.doctors = this.filterEntities;
    if (this.entityType === 'PATIENT') filter.patients = this.filterEntities;
    if (this.entityType === 'FACILITY') {
      filter.facilities = this.searchFacilitySelect.value?.length
        ? this.searchFacilitySelect.value.map((f) => f.id)
        : this.filterEntities;
    }
    if (this.fromDoctor) filter.doctors = [this.entity_id];
    if (this.fromFacility)
      // If from resident tab in facility list
      filter.facilities = [this.entity_id];
    filter.sortDirection = this.sort.direction || 'asc';
    filter.sortActive = this.sort.active || 'name';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deletePatient(_item: PatientModel) {
    const _title: string = this.translate.instant('PATIENT.DELETE_PATIENT_SIMPLE.TITLE');
    const _description: string = this.translate.instant(
      'PATIENT.DELETE_PATIENT_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'PATIENT.DELETE_PATIENT_SIMPLE.WAIT_DESCRIPTION',
    );
    const _deleteMessage = this.translate.instant('PATIENT.DELETE_PATIENT_SIMPLE.MESSAGE');

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    const dialogCloseSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new OnePatientOnServerDeleted({ id: _item.id }));
      const actionLoadingSubscription = this.store
        .pipe(select(selectPatientsActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectPatientErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error.code) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ message: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
                this.cdr.detectChanges();
              });
            this.subscriptions.push({ id: 7, sub: errorStateSubscription });
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.message);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Delete,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _deleteMessage,
                MessageType.Delete,
                3000,
                false,
                false,
              );
              this.loadPatientsList();
            }
            actionLoadingSubscription.unsubscribe();
          }
          this.cdr.detectChanges();
        });
      this.subscriptions.push({ id: 6, sub: actionLoadingSubscription });
    });
    this.subscriptions.push({ id: 5, sub: dialogCloseSubscription });
  }

  updateStatusForPatient(patient: PatientModel) {
    const _title: string = this.translate.instant('PATIENT.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant('PATIENT.UPDATE_STATUS_SIMPLE.DESCRIPTION');
    const _waitDesciption: string = this.translate.instant(
      'PATIENT.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );
    const _updateStatusSingleMessage = this.translate.instant(
      'PATIENT.UPDATE_STATUS_SIMPLE.MESSAGE',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDesciption);
    const dialogCloseSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newPatient: PatientModel = Object.assign(new PatientModel(), patient);
      newPatient.is_active = !newPatient.is_active;
      const accessObj = {
        entity_id: newPatient.id,
        access_from: 'Patient List',
        entity_type: 'PATIENT',
      };
      this.filteredViewService.createRecentSession(accessObj).subscribe();

      this.store.dispatch(
        new PatientStatusOnServerUpdated({
          patient: newPatient,
          id: newPatient.id,
          is_active: newPatient.is_active
        }),
      );

      const actionLoadingSubscription = this.store
        .pipe(select(selectPatientsActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectPatientErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error.code) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            this.subscriptions.push({ id: 10, sub: errorStateSubscription });
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.message);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Update,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _updateStatusSingleMessage,
                MessageType.Update,
                3000,
                false,
                false,
              );
            }
            actionLoadingSubscription.unsubscribe();
          }
        });
      this.subscriptions.push({ id: 9, sub: actionLoadingSubscription });
    });
    this.subscriptions.push({ id: 8, sub: dialogCloseSubscription });
  }

  addPatient() {
    this.navigationService.navigateTo('lists/patients/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewImageRequests() {
    this.navigationService.navigateTo('lists/patients/requests');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  editPatient(patient: PatientModel) {
    let queryParams = {};
    const accessObj = {
      entity_id: patient.id,
      access_from: 'Patient List',
      entity_type: 'PATIENT',
    };
    this.filteredViewService.createRecentSession(accessObj).subscribe();
    if (this.fromFacility || this.fromDoctor) {
      if (this.canvas) {
        this.taskService.newEntity.next({ id: patient.id, type: 'PATIENT' });
        return;
      }
      queryParams = {
        viewContext: ViewContext.patients,
        filteredView: this.filteredView || '',
        index: this.paginator.pageIndex,
        facility_id: this.fromFacility ? this.entity_id : '',
        doctor_id: this.fromDoctor ? this.entity_id : '',
      };
      this.navigationService.navigateTo('lists/patients/edit/' + patient.id, queryParams);
      this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
      return;
    }
    queryParams = {
      viewContext: ViewContext.patients,
      filteredView: this.filteredView || '',
      index: this.paginator.pageIndex,
    };
    this.navigationService.navigateTo('lists/patients/edit/' + patient.id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  canViewPatient(): boolean {
    return !!this.permissionsService.getPermission('canViewPatient');
  }

  canEditPatient(): boolean {
    return !!this.permissionsService.getPermission('canEditPatient');
  }

  viewPatient(patient: PatientModel) {
    if (!this.canViewPatient()) return;

    if (this.canEditPatient()) {
      this.editPatient(patient);
      return;
    }
    let queryParams;
    if (this.fromFacility || this.fromDoctor) {
      if (this.canvas) {
        this.taskService.newEntity.next({ id: patient.id, type: 'PATIENT' });
        return;
      }
      queryParams = {
        viewContext: ViewContext.patients,
        filteredView: this.filteredView || '',
        index: this.paginator.pageIndex,
        facility_id: this.fromFacility ? this.entity_id : '',
        doctor_id: this.fromDoctor ? this.entity_id : '',
      };
      this.navigationService.navigateTo('lists/patients/edit/' + patient.id, queryParams);
      this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
      return;
    }
    queryParams = {
      viewContext: ViewContext.patients,
      filteredView: this.filteredView || '',
      index: this.paginator.pageIndex,
      facility_id: this.fromFacility ? this.entity_id : '',
      doctor_id: this.fromDoctor ? this.entity_id : '',
    };
    this.navigationService.navigateTo('lists/patients/view/' + patient.id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewPatientDblclick(ref: string, patient: PatientModel): void {
    this.tabletUtil.handleDblclick(ref, this.viewPatient.bind(this, patient));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  onFileChange(event) {
    this.attachment = event.target.files[0].name;
    this.file = event.target.files[0];
    const split = this.attachment.split('.');
    const fileType = split.pop();
    if (fileType === 'csv') {
      const _saveMessage = this.attachment + ' Uploaded';
      if (event.target.files.length > 0) {
        const file = event.target.files[0];
        this.uploadForm.get('profile').setValue(file);
        const formData = new FormData();
        const datafile = this.uploadForm.get('profile').value;
        formData.append('file', new Blob([datafile], { type: 'text/csv' }), datafile.name);
        this.patientsService.uploadCSV(formData).subscribe();
        this.layoutUtilsService.showActionNotification(
          _saveMessage,
          MessageType.Update,
          4000,
          true,
          false,
        );
        this.uploadForm.get('profile').setValue(null);
        this.fileInput.nativeElement.value = null;
        this.loadPatientsList();
      }
    } else {
      this.fileInput.nativeElement.value = null;
      this.layoutUtilsService.showActionNotification(
        'Please upload only CSV files',
        MessageType.Update,
        4000,
        true,
        false,
      );
    }
  }

  onFileChange1(event) {
    this.attachment1 = event.target.files[0].name;
    this.file1 = event.target.files[0];
    const split = this.attachment1.split('.');
    const fileType = split.pop();
    if (fileType === 'csv') {
      const _saveMessage = this.attachment1 + ' Uploaded';
      if (event.target.files.length > 0) {
        const file = event.target.files[0];
        this.uploadForm1.get('profile1').setValue(file);
        const formData = new FormData();
        const datafile = this.uploadForm1.get('profile1').value;
        formData.append('file', new Blob([datafile], { type: 'text/csv' }), datafile.name);
        this.patientsService.uploadinsuranseCSV(formData).subscribe();
        this.layoutUtilsService.showActionNotification(
          _saveMessage,
          MessageType.Update,
          4000,
          true,
          false,
        );
        this.fileInput1.nativeElement.value = null;
      }
    } else {
      this.fileInput1.nativeElement.value = null;
      this.layoutUtilsService.showActionNotification(
        'Please upload only CSV files',
        MessageType.Update,
        4000,
        true,
        false,
      );
    }
  }

  showPatientPhoto(patient) {
    if (!patient.pre_signed_url?.url) {
      return;
    }
    this.dialog.open(ZoomInEntityComponent, { data: { entity: patient }, maxWidth: '600px' });
  }

  handleFacilityInput(event: Event): void {
    if (this.searchFacilityTimer) clearTimeout(this.searchFacilityTimer);

    this.searchFacilityTimer = setTimeout(
      () =>
        this.facilitiesService
          .searchFacilities(
            (event.target as HTMLInputElement)?.value,
            this.entityType === 'FACILITY' ? this.filterEntities : [],
          )
          .pipe(take(1))
          .subscribe((res) => {
            const selected = this.searchFacilitySelect?.value || [];
            const data =
              res?.data?.filter(
                (item) => !selected.map((facility) => facility.id).includes(item.id),
              ) || [];

            this.facilities$.next([...selected, ...data]);
            this.searchFacilitySelect.open();
          }),
      250,
    );
  }

  handleFacilityClose(): void {
    this.searchFacilityInput.nativeElement.value = this.searchFacilitySelect.value[0]?.name || '';
  }

  reloadPatientsList(): void {
    this.paginator.pageIndex = 0;

    this.loadPatientsList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadPatientsList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchEntityIdInput = '';
    this.searchNameEmailInput = '';
    this.searchDOBInput = '';
    this.searchDOBPicker?.select(undefined);
    this.searchCellPhoneInput = '';
    this.searchIsHoldInHospitalSelect = '';
    if (!this.fromFacility) {
      this.searchFacilityInput.nativeElement.value = '';
      this.searchFacilitySelect.value = [];
    }
  }
  emptyFacility(): void {
    this.searchFacilitySelect.value = [];
    this.searchFacilityInput.nativeElement.value = '';
  }
  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadPatientsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadPatientsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadPatientsList();
  }

  exportPatientsAsCSV(event: Event): void {
    event.stopPropagation();
    this.exporting = true;
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'name',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.layoutUtilsService.showActionNotification(
      'Export in progress, please wait ...',
      MessageType.Update,
      4000,
      true,
      false,
    );

    if (this.fromDoctor) {
      this.patientsService
        .getDoctorsPatientsAsCSV(queryParams, this.entity_id_for_task_list)
        .pipe(take(1))
        .subscribe((res) => {
          this.csvFile(res);
          this.exporting = false;
        });
      return;
    }
    if (this.fromFacility) {
      this.patientsService
        .getFacilitiesPatientsAsCSV(queryParams, this.entity_id_for_task_list)
        .pipe(take(1))
        .subscribe((res) => {
          this.csvFile(res);
          this.exporting = false;
        });
      return;
    }
    this.patientsService
      .getPatientsAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        this.csvFile(res);
        this.exporting = false;
      });
  }
  csvFile(res) {
    let listName = 'Patients List ';
    if (this.fromFacility) {
      listName = 'Residents List';
    }
    const filename = `${listName} List [${utc(new Date())
      .local()
      .format('YYYY-MM-DD, hh.mm a')}].csv`;
    this.downloadService.downloadFile(res, filename, 'text/csv');
  }
}
